import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Archive, ArrowLeft, Download, Plus, RefreshCw, Save, ShieldAlert, Sparkles, Zap } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const CATEGORIES = [
  { value: "workload", label: "Workload" },
  { value: "obstacles", label: "Obstacles" },
  { value: "growth_and_development", label: "Growth & Development" },
  { value: "upward_feedback", label: "Upward Feedback" },
  { value: "wellbeing_and_care", label: "Wellbeing & Care" },
  { value: "mission_alignment", label: "Mission Alignment" },
];

export default function RhythmAdmin() {
  const [tab, setTab] = useState("pairings");
  return (
    <div className="space-y-6" data-testid="rhythm-admin-page">
      <div>
        <Link to="/rhythm" className="text-sm text-forest-700 hover:text-forest-900 inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back To RHYTHM
        </Link>
        <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium mt-2">PO Admin</p>
        <h1 className="text-4xl font-serif text-forest-900">RHYTHM Admin</h1>
      </div>

      <nav className="flex flex-wrap gap-2">
        {["pairings", "audit", "seasons", "competencies", "questions", "starter-agenda", "blackouts", "notifications", "microcopy", "retention", "weekly-wins-reviewers"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`${tab === t ? "patria-btn-primary text-sm" : "patria-btn-secondary text-sm"} min-h-[44px]`}
            data-testid={`admin-tab-${t}`}
          >
            {t.split("-").map((x) => x.charAt(0).toUpperCase() + x.slice(1)).join(" ")}
          </button>
        ))}
      </nav>

      <section className="patria-card p-6">
        {tab === "pairings" && <PairingsTab />}
        {tab === "audit" && <AuditTab />}
        {tab === "seasons" && <SeasonsTab />}
        {tab === "competencies" && <CompetenciesTab />}
        {tab === "questions" && <QuestionsTab />}
        {tab === "starter-agenda" && <StarterAgendaTab />}
        {tab === "blackouts" && <BlackoutsTab />}
        {tab === "notifications" && <NotificationsTab />}
        {tab === "microcopy" && <MicrocopyTab />}
        {tab === "retention" && <RetentionTab />}
        {tab === "weekly-wins-reviewers" && <WeeklyWinsReviewersTab />}
      </section>
    </div>
  );
}

function PairingsTab() {
  const [pairings, setPairings] = useState([]);
  const [users, setUsers] = useState([]);
  const [unpaired, setUnpaired] = useState({ count: 0, rows: [] });
  const [form, setForm] = useState({ supervisor_id: "", employee_id: "", cadence: "biweekly" });
  const [supSearch, setSupSearch] = useState("");
  const [empSearch, setEmpSearch] = useState("");
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    const [p, u, up] = await Promise.all([
      api.get("/rhythm/pairings?include_archived=true"),
      api.get("/users?include_archived=false"),
      api.get("/rhythm/dashboard/unpaired").catch(() => ({ data: { count: 0, rows: [] } })),
    ]);
    setPairings(p.data);
    setUsers((u.data || []).filter((x) => x.role !== "volunteer" && !x.archived));
    setUnpaired(up.data);
  };
  useEffect(() => { load(); }, []);

  const supRoles = new Set(["manager", "campus_pastor", "po_admin", "super_admin"]);
  const supPool = useMemo(() => {
    const q = supSearch.trim().toLowerCase();
    return users.filter((u) => supRoles.has(u.role))
      .filter((u) => !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
  }, [users, supSearch]);
  const empPool = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    return users.filter((u) => !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
  }, [users, empSearch]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/rhythm/pairings", form);
      toast.success("Pairing created.");
      setForm({ supervisor_id: "", employee_id: "", cadence: "biweekly" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this pairing? It can be restored later.")) return;
    try { await api.post(`/rhythm/pairings/${id}/archive`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const generateMissing = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/rhythm/pairings/generate-missing");
      if (data.created === 0) {
        toast.success(`No new pairings needed — ${data.already_exists} already in place.`);
      } else {
        toast.success(`Created ${data.created} missing pairing${data.created === 1 ? "" : "s"}.`);
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't generate pairings.");
    } finally { setGenerating(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-serif text-2xl text-forest-900">Pairings</h2>
        <button
          type="button"
          className="patria-btn-secondary text-sm"
          onClick={generateMissing}
          disabled={generating}
          data-testid="btn-generate-missing-pairings"
        >
          <Zap className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
          {generating ? "Generating…" : "Generate Missing Pairings"}
        </button>
      </div>
      <p className="text-xs italic text-charcoal-muted mt-1">
        &ldquo;Generate Missing Pairings&rdquo; walks the Directory and creates a biweekly RHYTHM pairing for every
        active staff member with a manager assigned. Idempotent — safe to click twice.
      </p>

      <form onSubmit={create} className="mt-4 grid grid-cols-1 sm:grid-cols-5 gap-2" data-testid="new-pairing-form">
        <div className="sm:col-span-2">
          <input
            className="patria-input w-full mb-1"
            placeholder="Search supervisors…"
            value={supSearch}
            onChange={(e) => setSupSearch(e.target.value)}
            data-testid="pair-sup-search"
          />
          <select className="patria-input w-full" size={4} value={form.supervisor_id}
                  onChange={(e) => setForm({ ...form, supervisor_id: e.target.value })}
                  data-testid="pair-sup">
            {supPool.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <input
            className="patria-input w-full mb-1"
            placeholder="Search employees…"
            value={empSearch}
            onChange={(e) => setEmpSearch(e.target.value)}
            data-testid="pair-emp-search"
          />
          <select className="patria-input w-full" size={4} value={form.employee_id}
                  onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                  data-testid="pair-emp">
            {empPool.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
        </div>
        <div className="sm:col-span-1 flex flex-col gap-2">
          <select className="patria-input" value={form.cadence}
                  onChange={(e) => setForm({ ...form, cadence: e.target.value })}
                  data-testid="pair-cadence">
            <option value="weekly">Weekly</option>
            <option value="biweekly">Biweekly (Default)</option>
            <option value="monthly">Monthly</option>
          </select>
          <button type="submit" className="patria-btn-primary text-sm" data-testid="btn-create-pairing">
            <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Create
          </button>
        </div>
      </form>

      {/* Unpaired staff panel */}
      {unpaired.count > 0 && (
        <div className="mt-6 patria-card p-4 bg-cream-100" data-testid="unpaired-panel">
          <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
            Directory Gap
          </p>
          <p className="font-serif text-xl text-forest-900 mt-1">
            {unpaired.count} Active Staff Currently Unpaired
          </p>
          <p className="text-xs text-charcoal-muted mt-1">
            These teammates have a manager set in the Directory but no RHYTHM pairing yet.
            Click &ldquo;Generate Missing Pairings&rdquo; above to wire them up.
          </p>
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm" data-testid="unpaired-list">
            {unpaired.rows.slice(0, 20).map((r) => (
              <li key={r.id} className="p-2 rounded-lg bg-cream-200/60">
                <p className="text-forest-900 font-medium">{r.name}</p>
                <p className="text-xs text-charcoal-muted">{r.role} · {r.campus_name || "—"}</p>
              </li>
            ))}
          </ul>
          {unpaired.count > 20 && (
            <p className="text-[10px] italic text-charcoal-muted mt-2">
              +{unpaired.count - 20} more — showing the first 20.
            </p>
          )}
        </div>
      )}

      <table className="patria-table w-full mt-6 text-sm">
        <thead><tr><th>Supervisor</th><th>Employee</th><th>Cadence</th><th>Health</th><th>Next</th><th></th></tr></thead>
        <tbody>
          {pairings.map((p) => (
            <tr key={p.id} data-testid={`pair-row-${p.id}`}>
              <td>{p.supervisor_name}</td>
              <td>{p.employee_name}</td>
              <td className="capitalize">{p.effective_cadence}{p.compass_override && " · COMPASS"}</td>
              <td className="uppercase">{p.health}</td>
              <td className="text-xs">{p.next_scheduled}</td>
              <td className="text-right">
                {!p.archived && <button onClick={() => archive(p.id)} className="patria-btn-ghost text-terracotta-600" data-testid={`pair-archive-${p.id}`}><Archive className="h-4 w-4" strokeWidth={1.5} /> Archive</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function AuditTab() {
  const { backendUrl } = useAuth();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [filters, setFilters] = useState({ person_id: "", action: "", entity_type: "", start: "", end: "" });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/rhythm/audit", { params });
      setRows(data.rows);
      setCount(data.count);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't load audit log.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { api.get("/users?include_archived=false").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  const exportCsv = async () => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
    try {
      const res = await fetch(`${backendUrl}/api/rhythm/audit.csv?${qs.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "rhythm-audit-log.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Audit log CSV downloaded.");
    } catch { toast.error("Couldn't export the audit log."); }
  };

  const ACTION_OPTS = [
    "", "rhythm_meeting_start", "rhythm_meeting_edit_draft", "rhythm_meeting_finalize",
    "rhythm_meeting_acknowledge", "rhythm_meeting_addendum", "rhythm_direct_feedback_flag",
    "rhythm_view_meeting", "rhythm_view_pairing", "rhythm_pairing_create", "rhythm_pairing_edit",
    "rhythm_pairing_archive", "rhythm_pairing_generate_missing", "rhythm_agenda_add",
    "rhythm_agenda_archive", "rhythm_action_create", "rhythm_goal_create", "rhythm_win_add",
    "rhythm_meeting_export_pdf", "rhythm_settings_microcopy", "rhythm_settings_notifications",
    "rhythm_settings_retention",
  ];

  return (
    <div data-testid="rhythm-audit-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-serif text-2xl text-forest-900">Audit Log</h2>
          <p className="text-xs italic text-charcoal-muted mt-1">
            Every RHYTHM event — record created / drafted / finalized / acknowledged, addenda,
            direct-feedback flags, exports, and record views by non-participants. Append-only.
          </p>
        </div>
        <button
          type="button"
          onClick={exportCsv}
          className="patria-btn-secondary text-sm"
          data-testid="btn-export-audit-csv"
        >
          <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Export CSV
        </button>
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-5 gap-2">
        <select
          className="patria-input"
          value={filters.person_id}
          onChange={(e) => setFilters({ ...filters, person_id: e.target.value })}
          data-testid="audit-filter-person"
        >
          <option value="">Any Person</option>
          {users.filter((u) => u.role !== "volunteer" && !u.archived).map((u) => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
        <select
          className="patria-input"
          value={filters.action}
          onChange={(e) => setFilters({ ...filters, action: e.target.value })}
          data-testid="audit-filter-action"
        >
          {ACTION_OPTS.map((a) => (
            <option key={a} value={a}>{a || "Any Action"}</option>
          ))}
        </select>
        <input
          type="date"
          className="patria-input"
          value={filters.start}
          onChange={(e) => setFilters({ ...filters, start: e.target.value })}
          data-testid="audit-filter-start"
        />
        <input
          type="date"
          className="patria-input"
          value={filters.end}
          onChange={(e) => setFilters({ ...filters, end: e.target.value })}
          data-testid="audit-filter-end"
        />
        <button onClick={load} className="patria-btn-primary text-sm" data-testid="btn-audit-apply">
          <RefreshCw className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Apply
        </button>
      </div>
      <p className="text-xs text-charcoal-muted mt-3">
        {loading ? "Loading…" : `${count} event${count === 1 ? "" : "s"} matched.`}
      </p>
      <div className="mt-3 max-h-[520px] overflow-y-auto">
        <table className="patria-table w-full text-sm">
          <thead><tr><th>Timestamp</th><th>Actor</th><th>Action</th><th>Entity</th><th>Details</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="italic text-charcoal-muted p-4">No events yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                <td className="text-xs">{(r.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                <td>{r.actor_name || "system"}</td>
                <td className="text-xs">{(r.action || "").replaceAll("rhythm_", "").replaceAll("_", " ")}</td>
                <td className="text-xs">{r.entity_type}{r.entity_id ? ` · ${r.entity_id.slice(-6)}` : ""}</td>
                <td className="text-xs text-charcoal-muted max-w-md truncate">{r.details ? JSON.stringify(r.details) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SeasonsTab() {
  const [seasons, setSeasons] = useState([]);
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "" });
  const load = async () => setSeasons((await api.get("/rhythm/seasons")).data);
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/rhythm/seasons", form); toast.success("Season added."); setForm({ name: "", start_date: "", end_date: "" }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };
  return (
    <div>
      <h2 className="font-serif text-2xl text-forest-900">Seasons</h2>
      <form onSubmit={create} className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-2">
        <input className="patria-input" placeholder="Season name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="season-name" required />
        <input type="date" className="patria-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="season-start" required />
        <input type="date" className="patria-input" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="season-end" required />
        <button type="submit" className="patria-btn-primary" data-testid="btn-add-season"><Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add</button>
      </form>
      <ul className="mt-4 divide-y">{seasons.map((s) => (
        <li key={s.id} className="py-2 flex items-center justify-between"><span>{s.name}</span><span className="text-xs text-charcoal-muted">{s.start_date} → {s.end_date}</span></li>
      ))}</ul>
    </div>
  );
}

function CompetenciesTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", track: "professional" });
  const load = async () => setItems((await api.get("/rhythm/competencies")).data);
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/rhythm/competencies", form); toast.success("Added."); setForm({ name: "", description: "", track: "professional" }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };
  return (
    <div>
      <h2 className="font-serif text-2xl text-forest-900">Competencies</h2>
      <form onSubmit={create} className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-2">
        <input className="patria-input" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="comp-name" />
        <input className="patria-input sm:col-span-2" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="comp-desc" />
        <select className="patria-input" value={form.track} onChange={(e) => setForm({ ...form, track: e.target.value })} data-testid="comp-track">
          <option value="professional">Professional</option>
          <option value="ministry">Ministry</option>
        </select>
        <button type="submit" className="patria-btn-primary sm:col-span-4" data-testid="btn-add-competency"><Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add</button>
      </form>
      <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.map((c) => (
          <li key={c.id} className="p-3 rounded-xl bg-cream-100">
            <p className="font-medium text-forest-900">{c.name} <span className="patria-chip-green text-[10px] ml-2">{c.track}</span></p>
            <p className="text-xs text-charcoal-muted">{c.description}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function QuestionsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ category: "workload", text: "" });
  const load = async () => setItems((await api.get("/rhythm/questions")).data);
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/rhythm/questions", form); toast.success("Added."); setForm({ category: "workload", text: "" }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };
  return (
    <div>
      <h2 className="font-serif text-2xl text-forest-900">Question Banks</h2>
      <form onSubmit={create} className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-2">
        <select className="patria-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="q-category">
          {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <input className="patria-input sm:col-span-2" placeholder="Question…" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} required data-testid="q-text" />
        <button type="submit" className="patria-btn-primary" data-testid="btn-add-question"><Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add</button>
      </form>
      {CATEGORIES.map((c) => {
        const bucket = items.filter((q) => q.category === c.value);
        return (
          <div key={c.value} className="mt-4">
            <p className="text-xs uppercase tracking-widest text-charcoal-muted mb-1">{c.label} · {bucket.length}</p>
            <ul className="space-y-1">
              {bucket.slice(0, 12).map((q) => <li key={q.id} className="text-sm text-forest-900">&ldquo;{q.text}&rdquo;</li>)}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function NotificationsTab() {
  const [settings, setSettings] = useState(null);
  const load = async () => setSettings((await api.get("/rhythm/settings")).data);
  useEffect(() => { load(); }, []);
  const toggle = async (k, v) => {
    try {
      await api.put("/rhythm/settings/notifications", { [k]: v });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };
  if (!settings) return <p className="italic text-charcoal-muted">Loading…</p>;
  const toggles = settings.notification_toggles;
  const rows = Object.entries(toggles);
  return (
    <div data-testid="notifications-matrix">
      <h2 className="font-serif text-2xl text-forest-900">Notifications</h2>
      <p className="text-xs italic text-charcoal-muted mt-1">
        Each notification type can be turned on or off. Email is currently a
        <strong className="mx-1">mocked outbox</strong>
        (viewable under Settings → Outbound Email Log) — the toggle still governs
        whether we render an email row for tracking.
      </p>
      <div className="mt-4 overflow-x-auto">
        <table className="patria-table w-full text-sm">
          <thead>
            <tr>
              <th className="text-left">Notification Type</th>
              <th className="text-center">In-App</th>
              <th className="text-center">Email (Mocked Outbox)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k} data-testid={`notif-matrix-row-${k}`}>
                <td className="capitalize">{k.replaceAll("_", " ")}</td>
                <td className="text-center">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!v}
                      onChange={(e) => toggle(k, e.target.checked)}
                      className="h-5 w-5"
                      data-testid={`toggle-${k}`}
                    />
                  </label>
                </td>
                <td className="text-center">
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      v ? "bg-forest-600 text-cream-100" : "bg-sage-300 text-charcoal-muted"
                    }`}
                    data-testid={`email-status-${k}`}
                  >
                    {v ? "Mocked Send On" : "Off"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MicrocopyTab() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({});
  const load = async () => {
    const { data } = await api.get("/rhythm/settings");
    setSettings(data); setForm(data.microcopy);
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.put("/rhythm/settings/microcopy", form); toast.success("Saved."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };
  if (!settings) return <p className="italic text-charcoal-muted">Loading…</p>;
  return (
    <div>
      <h2 className="font-serif text-2xl text-forest-900">Microcopy</h2>
      <div className="mt-4 space-y-4">
        {[
          { key: "growth_plan_disclaimer", label: "Growth Plan Disclaimer" },
          { key: "notes_field_reminder", label: "Notes Field Reminder" },
          { key: "pastoral_prompt_text", label: "Pastoral Prompt Text" },
        ].map((f) => (
          <div key={f.key}>
            <label className="patria-label">{f.label}</label>
            <textarea
              className="patria-input w-full min-h-[80px]"
              value={form[f.key] || ""}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              data-testid={`micro-${f.key}`}
            />
          </div>
        ))}
      </div>
      <button onClick={save} className="patria-btn-primary mt-4" data-testid="btn-save-microcopy">
        <Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Save Microcopy
      </button>
    </div>
  );
}

function RetentionTab() {
  const [settings, setSettings] = useState(null);
  const [years, setYears] = useState(4);
  const [users, setUsers] = useState([]);
  const [holdUser, setHoldUser] = useState("");
  const [holdReason, setHoldReason] = useState("");
  const [purgeRows, setPurgeRows] = useState([]);
  const [purgeConfirm, setPurgeConfirm] = useState({ user_id: "", typed_name: "" });

  const load = async () => {
    const [s, u, p] = await Promise.all([
      api.get("/rhythm/settings"),
      api.get("/users?include_archived=true"),
      api.get("/rhythm/retention/purge-eligible").catch(() => ({ data: { rows: [] } })),
    ]);
    setSettings(s.data); setYears(s.data.retention_years);
    setUsers(u.data);
    setPurgeRows(p.data?.rows || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.put("/rhythm/settings/retention", { retention_years: Number(years) }); toast.success("Saved."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const setLegalHold = async (userId, hold, reason) => {
    try {
      await api.post(`/rhythm/legal-hold/${userId}`, { hold, reason: reason || null });
      toast.success(hold ? "Legal hold placed." : "Legal hold released.");
      setHoldUser(""); setHoldReason("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const purge = async () => {
    if (!purgeConfirm.user_id || !purgeConfirm.typed_name) return;
    try {
      await api.post("/rhythm/retention/purge", {
        user_id: purgeConfirm.user_id,
        confirm_typed_name: purgeConfirm.typed_name,
      });
      toast.success("Purge executed. Audit log updated.");
      setPurgeConfirm({ user_id: "", typed_name: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const usersOnHold = users.filter((u) => u.legal_hold);
  const activeUsers = users.filter((u) => !u.archived && u.role !== "volunteer");

  return (
    <div data-testid="retention-tab">
      <h2 className="font-serif text-2xl text-forest-900">Retention · Legal Hold · Purge</h2>
      <p className="text-xs text-charcoal-muted italic mt-1">
        California Government Code § 12946 requires personnel-file records to be
        retained at least 4 years after separation. Legal Hold pauses purge eligibility
        indefinitely.
      </p>

      {/* Retention window */}
      <div className="mt-6 p-4 rounded-2xl bg-cream-100" data-testid="retention-window-card">
        <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
          Retention Window
        </p>
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <input
            type="number" min={4} max={20}
            className="patria-input w-32"
            value={years}
            onChange={(e) => setYears(e.target.value)}
            data-testid="retention-years"
          />
          <span className="text-sm text-charcoal-soft">years after separation</span>
          <button onClick={save} className="patria-btn-primary min-h-[44px]" data-testid="btn-save-retention">
            <Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Save
          </button>
        </div>
      </div>

      {/* Legal Hold */}
      <div className="mt-6 p-4 rounded-2xl bg-cream-100" data-testid="legal-hold-card">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-terracotta-600" strokeWidth={1.5} />
          <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
            Legal Hold
          </p>
        </div>
        <p className="text-xs text-charcoal-muted mt-1">
          Place a hold when a person&rsquo;s records are relevant to an active complaint,
          claim, or litigation. Purge is blocked while a hold is in place.
        </p>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <select
            className="patria-input"
            value={holdUser}
            onChange={(e) => setHoldUser(e.target.value)}
            data-testid="legal-hold-user"
          >
            <option value="">Select a person…</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} · {u.employment_status || "active"}
              </option>
            ))}
          </select>
          <input
            className="patria-input"
            placeholder="Reason (kept internal / audit-only)"
            value={holdReason}
            onChange={(e) => setHoldReason(e.target.value)}
            data-testid="legal-hold-reason"
          />
          <button
            onClick={() => holdUser && setLegalHold(holdUser, true, holdReason)}
            disabled={!holdUser}
            className="patria-btn-primary min-h-[44px]"
            data-testid="btn-place-hold"
          >
            Place Hold
          </button>
        </div>

        <ul className="mt-4 space-y-2" data-testid="legal-hold-list">
          {usersOnHold.length === 0 && (
            <li className="text-xs italic text-charcoal-muted">No teammates on legal hold.</li>
          )}
          {usersOnHold.map((u) => (
            <li key={u.id} className="p-3 rounded-xl bg-terracotta-500/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid={`hold-row-${u.id}`}>
              <div className="min-w-0">
                <p className="text-forest-900 font-medium">{u.name}</p>
                <p className="text-xs text-charcoal-muted">
                  {u.legal_hold_reason || "No reason on file"}
                </p>
              </div>
              <button
                onClick={() => setLegalHold(u.id, false, null)}
                className="patria-btn-ghost text-terracotta-700 min-h-[44px]"
                data-testid={`btn-release-hold-${u.id}`}
              >
                Release Hold
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Purge-eligible */}
      <div className="mt-6 p-4 rounded-2xl bg-cream-100" data-testid="purge-card">
        <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
          Purge · Super Admin Only
        </p>
        <p className="text-xs text-charcoal-muted mt-1">
          Separated teammates past the retention window and not under legal hold are
          eligible for purge. Purge is an <strong>audit-logged final step</strong> and
          only Super Admin can confirm.
        </p>
        <ul className="mt-3 space-y-2" data-testid="purge-eligible-list">
          {purgeRows.length === 0 && (
            <li className="text-xs italic text-charcoal-muted">Nothing eligible right now.</li>
          )}
          {purgeRows.map((r) => (
            <li key={r.user_id} className="p-3 rounded-xl bg-cream-200/60" data-testid={`purge-row-${r.user_id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-forest-900 font-medium">{r.name}</p>
                  <p className="text-xs text-charcoal-muted">
                    Separated {r.separated_at?.slice(0, 10) || "—"} · eligible {r.eligible_date || "—"}
                  </p>
                </div>
                {r.eligible ? (
                  <button
                    onClick={() => setPurgeConfirm({ user_id: r.user_id, typed_name: "" })}
                    className="patria-btn-secondary text-sm min-h-[44px]"
                    data-testid={`btn-open-purge-${r.user_id}`}
                  >
                    Confirm Purge…
                  </button>
                ) : (
                  <span className="text-[10px] uppercase tracking-widest text-charcoal-muted">
                    Not yet eligible
                  </span>
                )}
              </div>
              {purgeConfirm.user_id === r.user_id && (
                <div className="mt-3 border-t border-sage-300/60 pt-3">
                  <p className="text-xs text-terracotta-700">
                    Type <strong>{r.name}</strong> to confirm. This is permanent.
                  </p>
                  <div className="mt-2 flex flex-col sm:flex-row gap-2">
                    <input
                      className="patria-input flex-1"
                      value={purgeConfirm.typed_name}
                      onChange={(e) => setPurgeConfirm({ ...purgeConfirm, typed_name: e.target.value })}
                      data-testid={`purge-typed-name-${r.user_id}`}
                    />
                    <button
                      onClick={purge}
                      disabled={purgeConfirm.typed_name !== r.name}
                      className="patria-btn-primary min-h-[44px]"
                      data-testid={`btn-confirm-purge-${r.user_id}`}
                    >
                      Purge Records
                    </button>
                    <button
                      onClick={() => setPurgeConfirm({ user_id: "", typed_name: "" })}
                      className="patria-btn-ghost min-h-[44px]"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function BlackoutsTab() {
  const [rows, setRows] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [form, setForm] = useState({ name: "", campus_id: "", start_date: "", end_date: "", note: "" });
  const load = async () => {
    const [b, c] = await Promise.all([
      api.get("/rhythm/blackout-windows?include_archived=true"),
      api.get("/campuses"),
    ]);
    setRows(b.data);
    setCampuses(c.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.start_date || !form.end_date) return;
    try {
      await api.post("/rhythm/blackout-windows", {
        ...form,
        campus_id: form.campus_id || null,
      });
      toast.success("Blackout window added.");
      setForm({ name: "", campus_id: "", start_date: "", end_date: "", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this blackout window?")) return;
    try {
      await api.post(`/rhythm/blackout-windows/${id}/archive`);
      toast.success("Archived.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const active = rows.filter((r) => !r.archived);
  const archived = rows.filter((r) => r.archived);

  return (
    <div data-testid="blackouts-tab">
      <h2 className="font-serif text-2xl text-forest-900">Cadence Blackout Windows</h2>
      <p className="text-xs italic text-charcoal-muted mt-1">
        During a blackout window, 1:1 cadence math is paused for the targeted
        campus (or all campuses). Nudges stop firing and pairings display as
        &ldquo;Paused · blackout&rdquo;. Typical uses: Holy Week, Christmas + New
        Year, all-staff retreats.
      </p>

      <form onSubmit={create} className="mt-4 grid grid-cols-1 sm:grid-cols-6 gap-2" data-testid="blackout-create-form">
        <input
          className="patria-input sm:col-span-2"
          placeholder="Window name (e.g., Christmas Week)"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="blackout-name"
          required
        />
        <select
          className="patria-input"
          value={form.campus_id}
          onChange={(e) => setForm({ ...form, campus_id: e.target.value })}
          data-testid="blackout-campus"
        >
          <option value="">All Campuses</option>
          {campuses.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
        </select>
        <input
          type="date"
          className="patria-input"
          value={form.start_date}
          onChange={(e) => setForm({ ...form, start_date: e.target.value })}
          data-testid="blackout-start"
          required
        />
        <input
          type="date"
          className="patria-input"
          value={form.end_date}
          onChange={(e) => setForm({ ...form, end_date: e.target.value })}
          data-testid="blackout-end"
          required
        />
        <button
          type="submit"
          className="patria-btn-primary min-h-[44px]"
          data-testid="btn-add-blackout"
        >
          <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add
        </button>
        <input
          className="patria-input sm:col-span-6"
          placeholder="Internal note (optional)"
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          data-testid="blackout-note"
        />
      </form>

      <h3 className="mt-6 font-serif text-xl text-forest-900">Active</h3>
      <ul className="mt-3 space-y-2" data-testid="blackout-active-list">
        {active.length === 0 && (
          <li className="text-xs italic text-charcoal-muted">No active blackout windows.</li>
        )}
        {active.map((b) => {
          const camp = campuses.find((c) => c.id === b.campus_id);
          return (
            <li key={b.id} className="p-3 rounded-xl bg-cream-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid={`blackout-row-${b.id}`}>
              <div className="min-w-0">
                <p className="text-forest-900 font-medium">{b.name}</p>
                <p className="text-xs text-charcoal-muted">
                  {b.start_date} → {b.end_date} · {camp ? camp.name : "All Campuses"}
                </p>
                {b.note && <p className="text-xs italic text-charcoal-muted mt-1">{b.note}</p>}
              </div>
              <button
                onClick={() => archive(b.id)}
                className="patria-btn-ghost text-terracotta-600 min-h-[44px]"
                data-testid={`btn-archive-blackout-${b.id}`}
              >
                <Archive className="h-4 w-4 mr-1" strokeWidth={1.5} /> Archive
              </button>
            </li>
          );
        })}
      </ul>

      {archived.length > 0 && (
        <>
          <h3 className="mt-6 font-serif text-xl text-forest-900">Archived</h3>
          <ul className="mt-3 space-y-2 opacity-70">
            {archived.map((b) => (
              <li key={b.id} className="p-3 rounded-xl bg-cream-200/50 text-sm text-charcoal-muted">
                {b.name} · {b.start_date} → {b.end_date}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function StarterAgendaTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/rhythm/settings");
      setItems(data.starter_agenda_items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      await api.put("/rhythm/settings/starter-agenda", { items: next });
      setItems(next);
      toast.success("Saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const addItem = () => {
    const v = newItem.trim();
    if (!v) return;
    if (items.length >= 12) { toast.error("Max 12 starter items."); return; }
    save([...items, v]);
    setNewItem("");
  };

  const removeAt = (i) => save(items.filter((_, idx) => idx !== i));

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    save(next);
  };

  if (loading) return <p className="italic text-charcoal-muted">Loading…</p>;
  return (
    <div>
      <h2 className="font-serif text-2xl text-forest-900">Starter Agenda</h2>
      <p className="text-xs text-charcoal-muted italic mt-1">
        These items populate when someone clicks &ldquo;Populate A Starter Agenda&rdquo; on an empty 1:1.
        Max 12. Reorder with the arrows.
      </p>
      <ul className="mt-4 space-y-2" data-testid="starter-agenda-list">
        {items.map((it, i) => (
          <li key={`${it}-${i}`} className="p-3 rounded-xl bg-cream-100 flex items-center justify-between gap-2">
            <span className="text-forest-900">{it}</span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="patria-btn-ghost text-xs"
                onClick={() => move(i, -1)}
                disabled={saving || i === 0}
                data-testid={`starter-up-${i}`}
              >
                ↑
              </button>
              <button
                type="button"
                className="patria-btn-ghost text-xs"
                onClick={() => move(i, 1)}
                disabled={saving || i === items.length - 1}
                data-testid={`starter-down-${i}`}
              >
                ↓
              </button>
              <button
                type="button"
                className="patria-btn-ghost text-terracotta-600 text-xs"
                onClick={() => removeAt(i)}
                disabled={saving}
                data-testid={`starter-remove-${i}`}
              >
                Remove
              </button>
            </div>
          </li>
        ))}
        {items.length === 0 && (
          <li className="text-sm italic text-charcoal-muted">
            No starter items yet — add a few warm, gentle prompts below.
          </li>
        )}
      </ul>
      <div className="mt-4 flex items-center gap-2">
        <input
          className="patria-input flex-1"
          placeholder="Add A Starter Item (e.g., Wins Since We Last Met)"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
          data-testid="starter-new-item"
        />
        <button
          type="button"
          className="patria-btn-primary"
          onClick={addItem}
          disabled={saving || !newItem.trim()}
          data-testid="btn-add-starter"
        >
          <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add
        </button>
      </div>
    </div>
  );
}

function WeeklyWinsReviewersTab() {
  const [reviewers, setReviewers] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState("");
  const [goal, setGoal] = useState(null);
  const [goalForm, setGoalForm] = useState({ current_score: "", target_score: "", deadline: "" });

  const load = async () => {
    const [r, u, g] = await Promise.all([
      api.get("/weekly-wins/permissions/reviewers"),
      api.get("/users?include_archived=false"),
      api.get("/weekly-wins/goal").catch(() => ({ data: null })),
    ]);
    setReviewers(r.data);
    setUsers((u.data || []).filter((x) => x.role !== "volunteer" && !x.archived));
    if (g.data) {
      setGoal(g.data);
      setGoalForm({
        current_score: String(g.data.current_score ?? ""),
        target_score: String(g.data.target_score ?? ""),
        deadline: g.data.deadline || "",
      });
    }
  };
  useEffect(() => { load(); }, []);

  const grant = async () => {
    if (!selected) return;
    try {
      await api.post(`/weekly-wins/permissions/reviewers/${selected}`);
      toast.success("Reviewer permission granted.");
      setSelected(""); setSearch("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke Weekly Wins Reviewer permission?")) return;
    try {
      await api.delete(`/weekly-wins/permissions/reviewers/${id}`);
      toast.success("Reviewer permission revoked.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const saveGoal = async () => {
    try {
      const payload = {};
      if (goalForm.current_score !== "") payload.current_score = Number(goalForm.current_score);
      if (goalForm.target_score !== "") payload.target_score = Number(goalForm.target_score);
      if (goalForm.deadline) payload.deadline = goalForm.deadline;
      await api.put("/weekly-wins/goal", payload);
      toast.success("Goal updated.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const q = search.trim().toLowerCase();
  const pool = users
    .filter((u) => !reviewers.some((r) => r.user_id === u.id))
    .filter((u) => !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));

  return (
    <div data-testid="ww-reviewers-tab">
      <h2 className="font-serif text-2xl text-forest-900">Weekly Wins Reviewers</h2>
      <p className="text-xs italic text-charcoal-muted mt-1">
        A Weekly Wins Reviewer sees every campus&rsquo;s report, including Leader Pulse
        content. Grant this permission carefully. PO Admin + Super Admin already have
        review access — this is for pastoral leaders (like Aaron) who aren&rsquo;t PO.
      </p>

      <div className="mt-6 p-4 rounded-2xl bg-cream-100">
        <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
          The Goal
        </p>
        <p className="text-xs text-charcoal-muted mt-1">
          Displayed on every Weekly Wins form + roll-up. Reviewers can update it here.
        </p>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-2">
          <input
            type="number" min={0} max={100}
            className="patria-input"
            placeholder="Current score"
            value={goalForm.current_score}
            onChange={(e) => setGoalForm({ ...goalForm, current_score: e.target.value })}
            data-testid="ww-goal-current"
          />
          <input
            type="number" min={0} max={100}
            className="patria-input"
            placeholder="Target score"
            value={goalForm.target_score}
            onChange={(e) => setGoalForm({ ...goalForm, target_score: e.target.value })}
            data-testid="ww-goal-target"
          />
          <input
            type="date"
            className="patria-input"
            value={goalForm.deadline}
            onChange={(e) => setGoalForm({ ...goalForm, deadline: e.target.value })}
            data-testid="ww-goal-deadline"
          />
          <button onClick={saveGoal} className="patria-btn-primary" data-testid="btn-save-ww-goal">
            <Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Save Goal
          </button>
        </div>
      </div>

      <div className="mt-6 p-4 rounded-2xl bg-cream-100" data-testid="ww-grant-panel">
        <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
          Grant Reviewer Permission
        </p>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <input
            className="patria-input sm:col-span-1"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="ww-grant-search"
          />
          <select
            className="patria-input sm:col-span-1"
            size={4}
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            data-testid="ww-grant-select"
          >
            {pool.slice(0, 40).map((u) => (
              <option key={u.id} value={u.id}>{u.name} · {u.role} · {u.email}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={grant}
            disabled={!selected}
            className="patria-btn-primary sm:col-span-1 min-h-[44px]"
            data-testid="btn-ww-grant-reviewer"
          >
            <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Grant Permission
          </button>
        </div>
      </div>

      <h3 className="mt-6 font-serif text-xl text-forest-900">Current Reviewers</h3>
      <ul className="mt-3 space-y-2" data-testid="ww-reviewers-list">
        {reviewers.length === 0 && (
          <li className="text-xs italic text-charcoal-muted">
            No non-PO reviewers yet. PO Admin + Super Admin always have review access.
          </li>
        )}
        {reviewers.map((r) => (
          <li
            key={r.user_id}
            className="p-3 rounded-xl bg-sage-200/40 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
            data-testid={`ww-reviewer-row-${r.user_id}`}
          >
            <div className="min-w-0">
              <p className="text-forest-900 font-medium">{r.name}</p>
              <p className="text-xs text-charcoal-muted">
                {r.email} · granted {(r.granted_at || "").slice(0, 10)} by {r.granted_by || "system"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => revoke(r.user_id)}
              className="patria-btn-ghost text-terracotta-600 min-h-[44px]"
              data-testid={`btn-ww-revoke-${r.user_id}`}
            >
              Revoke
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

